import {css} from '@emotion/react';
import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import type {SnsIconKey} from '../../utils/getSnsIcon';

import Curve1 from '../../assets/images/Curve1';
import Curve2 from '../../assets/images/Curve2';
import ExtendedNavLink from '../../components/ExtendedNavLink';
import {linkToApp} from '../../lib/url/linkToApp';
import {
  linkToAllizInstagram,
  linkToAllizLinkedin,
  linkToAllizNote,
  linkToAllizRedNote,
  linkToAllizX,
  linkToAllizYoutube,
  linkToMarketing,
} from '../../lib/url/linkToMarketing';
import useAppTheme from '../../styles/useAppTheme';
import useColor from '../../styles/useColor';
import useComponent from '../../styles/useComponent';
import useTypography from '../../styles/useTypography';
import getSnsIcon from '../../utils/getSnsIcon';

/**
 * Renders the marketing footer section of the application, including navigation links,
 * social media icons, a call-to-action button, and branding elements. The layout is responsive
 * and adapts to various breakpoints using theme-based styles.
 *
 * @remark This needs to be revised once the layout component is refactored.
 */
const FooterSection = () => {
  const {breakpoints, colors, font, spacings} = useAppTheme();
  const {buttons, page} = useComponent();
  const {palette} = useColor();
  const {body, heading} = useTypography();
  const {t} = useTranslation();

  const layoutSmBreakPoint = breakpoints.sm;
  const layoutMdBreakPoint = breakpoints.md;
  const layoutLgBreakPoint = breakpoints.lg;
  const layoutXlBreakPoint = breakpoints.xl;

  const topSectionCss = css({
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: palette.background.strong.defaultColor,
  });

  const curve1ContainerCss = css({
    marginTop: `-${spacings.px40}`,
    marginBottom: `-${spacings.px80}`,
    [layoutMdBreakPoint]: {
      position: 'absolute',
    },
    [layoutXlBreakPoint]: {
      marginTop: `-${spacings.px10}`,
      marginBottom: `-${spacings.px40}`,
    },
  });

  const curve1Css = css({
    minWidth: spacings.px375,
    minHeight: spacings.px230,
  });

  const sectionContainerCss = css([
    page.lg,
    {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      rowGap: spacings.px32,
      paddingTop: spacings.px40,
      paddingBottom: spacings.px40,
      justifySelf: 'center',
      position: 'relative',
      overflow: 'hidden',
      [layoutMdBreakPoint]: {
        gridTemplateColumns: 'auto minmax(0, max-content)',
        columnGap: spacings.px48,
      },
      [layoutLgBreakPoint]: {
        gridTemplateColumns: 'auto auto',
        paddingLeft: spacings.px180,
        paddingRight: spacings.px180,
      },
      [layoutXlBreakPoint]: {
        paddingBottom: spacings.px90,
        paddingTop: spacings.px90,
        gridTemplateColumns: 'auto max-content max-content',
      },
    },
  ]);

  const navCss = css({
    display: 'contents',
    [layoutMdBreakPoint]: {
      display: 'flex',
      gap: spacings.px32,
      justifyContent: 'end',
      gridColumn: '1 / -1',
    },
    [layoutLgBreakPoint]: {
      gridRow: 1,
      justifyContent: 'start',
    },
    [layoutXlBreakPoint]: {
      gridColumn: 1,
    },
    [breakpoints.xxl]: {
      gap: spacings.px48,
    },
  });

  const topNavListCss = css([
    body.s,
    {
      display: 'contents',
      fontWeight: font.weight.bold,
      gap: spacings.px80,
      color: palette.content.white.defaultColor,
    },
  ]);

  const snsListCss = css({
    display: 'flex',
    gap: spacings.px12,
    color: colors['brown-400'],
    justifySelf: 'center',
    alignSelf: 'center',
    gridColumn: 'span 2',
    [layoutSmBreakPoint]: {justifySelf: 'end', gridColumn: 2},
    [layoutMdBreakPoint]: {
      gridColumn: 2,
    },
    [layoutLgBreakPoint]: {
      gridColumn: 1,
      gridRow: 2,
      justifySelf: 'start',
      gap: spacings.px24,
    },
  });

  const snsItemCss = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '100%',
    width: spacings.px32,
    height: spacings.px32,
    fontSize: spacings.px19,
    backgroundColor: palette.background.neutral.defaultColor,
    [layoutLgBreakPoint]: {
      width: spacings.px40,
      height: spacings.px40,
      fontSize: spacings.px22,
    },
  });

  const joinButtonCss = css([
    buttons.gradient.l,
    {
      gridColumn: 'span 2',
      justifySelf: 'center',
      [layoutSmBreakPoint]: {gridColumn: 1, justifySelf: 'start'},
      [layoutMdBreakPoint]: {gridColumn: 1, justifySelf: 'end'},
      [layoutLgBreakPoint]: {gridRow: 2, gridColumn: 2},
      [layoutXlBreakPoint]: {
        gridRow: '1 / span 2',
        gridColumn: 3,
        alignSelf: 'center',
      },
    },
  ]);

  const dividerCss = css({
    gridColumn: '1 / -1',
    [layoutXlBreakPoint]: {display: 'none'},
  });

  const logoContainerCss = css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacings.px10,
    gridColumn: '1 / -1',
    [layoutXlBreakPoint]: {
      alignItems: 'start',
      gridColumn: 2,
      gridRow: '1 / span 2',
    },
  });

  const logoTextCss = css([
    heading.s,
    {
      textAlign: 'center',
      color: colors['brown-400'],
      [layoutXlBreakPoint]: {fontSize: spacings.px20, textAlign: 'start'},
    },
  ]);

  const curve2ContainerCss = css({
    display: 'none',
    [layoutMdBreakPoint]: {
      display: 'block',
      position: 'absolute',
      right: 0,
      bottom: 0,
    },
  });

  const curve2Css = css({
    minWidth: spacings.px230,
    minHeight: spacings.px240,
    transform: `translateY(${spacings.px15})`,
  });

  const renderNavItems = () => {
    const items: {label: string; to: string}[] = [
      {
        label: t('pages.about.about', {brand: t('common.brand')}),
        to: linkToMarketing('/about'),
      },
      {label: t('nav.events'), to: linkToMarketing('/event')},
      {
        label: t('pages.community.our-community'),
        to: linkToMarketing('/community'),
      },
      {label: t('pages.faq.faq'), to: linkToMarketing('/faq')},
      {
        label: t('pages.contact.contact-page-title'),
        to: linkToMarketing('/contact'),
      },
    ];

    return items.map((item, index) => (
      <li key={index}>
        <ExtendedNavLink to={item.to}>{item.label}</ExtendedNavLink>
      </li>
    ));
  };

  const renderSnsItems = () => {
    const sns: Partial<Record<SnsIconKey, string>> = {
      linkedin: linkToAllizLinkedin(),
      instagram: linkToAllizInstagram(),
      x: linkToAllizX(),
      rednote: linkToAllizRedNote(),
      youtube: linkToAllizYoutube(),
      note: linkToAllizNote(),
    };

    return Object.keys(sns).map((icon, index) => {
      const Icon = getSnsIcon(icon as SnsIconKey);
      return (
        <li key={index}>
          <Link
            css={snsItemCss}
            target="_blank"
            to={sns[icon as SnsIconKey] as string}
          >
            <Icon aria-label={icon} />
          </Link>
        </li>
      );
    });
  };

  return (
    <section css={topSectionCss}>
      <div css={curve1ContainerCss}>
        <Curve1 css={curve1Css} />
      </div>
      <div css={sectionContainerCss}>
        <nav css={navCss}>
          <ul css={topNavListCss}>{renderNavItems()}</ul>
        </nav>
        <Link
          css={joinButtonCss}
          to={linkToApp('/login')}
        >
          {t('nav.signup')}
        </Link>
        <ul css={snsListCss}>{renderSnsItems()}</ul>
        <hr css={dividerCss} />
        <div css={logoContainerCss}>
          <img
            alt="alliz logo"
            src="/static/images/brand-logo.svg"
          />
          <span css={logoTextCss}>
            <Trans i18nKey="footer.footer-cta-label" />
          </span>
        </div>
      </div>
      <div css={curve2ContainerCss}>
        <Curve2 css={curve2Css} />
      </div>
    </section>
  );
};

export default FooterSection;
